/** Billing and cash desk: small helpers shared by the invoices, invoice, cash desk and settings screens. */

import type { InvoiceStatusEnum, PaymentMethodEnum, PaymentStatusEnum } from "@agencyos/api-client";
import type { BadgeTone, StatTone } from "@agencyos/ui-web";
import { StatCard } from "@agencyos/ui-web";
import { useQueryClient } from "@tanstack/react-query";
import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";

import { useCurrentRole } from "../../lib/settings";

export const TIME_ZONE = "Africa/Douala";
export const INVOICE_STATUSES: InvoiceStatusEnum[] = ["draft", "issued", "partial", "paid", "accepted", "cancelled"];
export const PAYMENT_STATUSES: PaymentStatusEnum[] = ["pending", "confirmed", "failed", "cancelled"];
export const PAYMENT_METHODS: PaymentMethodEnum[] = ["cash", "momo_mtn", "momo_orange", "transfer", "deposit"];
/** Entered at the office; Mobile Money goes through a payment request, or is entered by hand
 * when the client already sent it to the agency's number (« hors ligne », D-126). */
export const OFFICE_METHODS = ["cash", "transfer", "deposit", "momo_mtn", "momo_orange"] as const;
export type OfficeMethod = (typeof OFFICE_METHODS)[number];

export function useBillingRoles() {
  const role = useCurrentRole();
  return {
    role,
    /** Owner, manager, advisor (their clients) and accountant (D-126) record payments and issue invoices. */
    canCollect: role === "owner" || role === "manager" || role === "advisor" || role === "accountant",
    /** Owner, manager and accountant (D-126) cancel payments and invoices (credit note). */
    canCancel: role === "owner" || role === "manager" || role === "accountant",
    canExport: role === "owner" || role === "manager" || role === "accountant",
  };
}

export function useRefreshBilling() {
  const queryClient = useQueryClient();
  return () =>
    void queryClient.invalidateQueries({
      predicate: (query) => String(query.queryKey[0] ?? "").startsWith("/api/v1/billing/"),
    });
}

export function localeOf(language: string): string {
  return language === "en" ? "en-GB" : language;
}

/** Today in Douala, as YYYY-MM-DD. */
export function todayInDouala(): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: TIME_ZONE }).format(new Date());
}

/** Month in Douala of an ISO timestamp, as YYYY-MM. */
export function monthInDouala(iso: string): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: TIME_ZONE }).format(new Date(iso)).slice(0, 7);
}

export function dateText(value: string | null | undefined, language: string): string {
  if (!value) return "—";
  // A bare date (YYYY-MM-DD) is read at noon UTC so it never shifts by a day.
  const date = /^\d{4}-\d{2}-\d{2}$/.test(value) ? new Date(`${value}T12:00:00Z`) : new Date(value);
  return date.toLocaleDateString(localeOf(language), { timeZone: TIME_ZONE });
}

export function dateTimeText(value: string | null | undefined, language: string): string {
  if (!value) return "—";
  return new Date(value).toLocaleString(localeOf(language), {
    timeZone: TIME_ZONE,
    dateStyle: "short",
    timeStyle: "short",
  });
}

/** 1925 → « 19,25 % » (or « 19.25 % » in English). */
export function rateText(rateBp: number, language: string): string {
  return `${new Intl.NumberFormat(localeOf(language), { maximumFractionDigits: 2 }).format(rateBp / 100)} %`;
}

export function invoiceTone(status: InvoiceStatusEnum): BadgeTone {
  if (status === "paid" || status === "accepted") return "success";
  if (status === "cancelled") return "danger";
  if (status === "partial") return "offer";
  return "default";
}

export function paymentTone(status: PaymentStatusEnum): BadgeTone {
  if (status === "confirmed") return "success";
  if (status === "failed" || status === "cancelled") return "danger";
  return "offer";
}

export function isOverdue(invoice: { kind: string; status: InvoiceStatusEnum; due_date: string | null }): boolean {
  return (
    invoice.kind === "invoice" &&
    (invoice.status === "issued" || invoice.status === "partial") &&
    invoice.due_date !== null &&
    invoice.due_date < todayInDouala()
  );
}

/** Digits only → integer amount in FCFA (NaN when empty). */
export function amountOf(text: string): number {
  const digits = text.replace(/\D/g, "");
  return digits ? Number.parseInt(digits, 10) : Number.NaN;
}

/** Key figure of the billing screens (design v2.1: StatCard, the tone tints the icon tile). */
export function Figure({ icon: Icon, label, value, hint, tone = "accent" }: { icon: LucideIcon; label: string; value: ReactNode; hint?: string; tone?: StatTone }) {
  // Amounts (« 4 566 667 FCFA ») take a whole row on a phone; counts share it.
  return <StatCard className={typeof value === "string" ? "kpi kpi--money" : "kpi"} tone={tone} icon={<Icon size={18} strokeWidth={1.75} />} label={label} value={value} hint={hint} />;
}

export function Pager({
  page,
  hasNext,
  onChange,
  labels,
}: {
  page: number;
  hasNext: boolean;
  onChange: (page: number) => void;
  labels: { previous: string; next: string; page: string };
}) {
  if (page === 1 && !hasNext) return null;
  return (
    <div className="row between">
      <button type="button" className="ao-button ao-button--ghost" disabled={page === 1} onClick={() => onChange(page - 1)}>
        {labels.previous}
      </button>
      <span className="muted">{labels.page}</span>
      <button type="button" className="ao-button ao-button--ghost" disabled={!hasNext} onClick={() => onChange(page + 1)}>
        {labels.next}
      </button>
    </div>
  );
}
